import { useEffect, useState } from 'react';
import type { AuthContextProps } from 'react-oidc-context';

const RETRY_DELAY_IN_MS = 2_000;
const MAX_RETRIES = 2;

// ADFS lets the refresh token expire without issuing a new one upon token refresh
// Therefore, when we catch this error, we remove the user from the store and re-authenticate silently
// (signinSilent uses the refresh token if it exists, therefore we need to remove it first)
// As suggested here: https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/5315
export function useAdfsExpiredRefreshTokenHandling(auth: AuthContextProps) {
    const [isProcessing, setIsProcessing] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        (async function () {
            if (auth.error && auth.error.message.includes('MSIS9615') && !isProcessing && retryCount <= MAX_RETRIES) {
                // the `auth.error` will still be present until re-authentication finished; until then we need to prevent the code from re-executing
                setIsProcessing(true);

                try {
                    await auth.removeUser();
                    await auth.signinSilent();

                    setRetryCount(0);
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                } catch (e) {
                    setRetryCount((c) => c + 1);
                    await delay(RETRY_DELAY_IN_MS);
                } finally {
                    setIsProcessing(false);
                }
            }
        })();
    }, [auth, isProcessing, retryCount]);
}

async function delay(timeInMs: number) {
    return new Promise<void>((resolve) => {
        setTimeout(() => resolve(), timeInMs);
    });
}
